import { useDownloadFile } from '@newfront-insurance/admin-ui/files';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useNotify } from '@newfront-insurance/next-notifications';
import { useCallback, useState } from 'react';

import { normalizeSkillCommand } from '../../../lib/skill-command-utils';
import type { CustomSkillRecord } from '../../../queries/chats';
import { useChatOverlayContextSafe } from '../contexts/chat-overlay-context';
import { NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../lib/analytics';

const CUSTOM_SKILL_ZIP_UNAVAILABLE_MESSAGE = 'This custom skill does not have a ZIP file available to download.';

export function getCustomSkillDownloadFilename(skill: { command: string }) {
  return `${normalizeSkillCommand(skill.command) || 'custom-skill'}.zip`;
}

function getCustomSkillDownloadErrorMessage(error: unknown) {
  return error instanceof Error && error.message ? error.message : 'Unable to download custom skill ZIP.';
}

export function useCustomSkillDownload() {
  const analytics = useAnalytics();
  const notify = useNotify();
  const chatOverlayContext = useChatOverlayContextSafe();
  const { download } = useDownloadFile({});
  const [downloadingSkillUuid, setDownloadingSkillUuid] = useState<string | null>(null);

  const downloadCustomSkill = useCallback(
    async (skill: CustomSkillRecord) => {
      if (!skill.sourceFileUuid) {
        notify.error(CUSTOM_SKILL_ZIP_UNAVAILABLE_MESSAGE);
        return;
      }

      setDownloadingSkillUuid(skill.uuid);
      try {
        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CUSTOM_SKILL_DOWNLOADED, {
          category: skill.frontmatter.category,
          chatUuid: chatOverlayContext?.chatUuid,
          commandHandle: skill.command,
          fileExtension: 'zip',
          fileUuid: skill.sourceFileUuid,
          skillId: skill.uuid,
          skillSource: skill.source,
        });

        await download(skill.sourceFileUuid, getCustomSkillDownloadFilename(skill));
      } catch (downloadError) {
        notify.error(getCustomSkillDownloadErrorMessage(downloadError));
      } finally {
        setDownloadingSkillUuid(null);
      }
    },
    [analytics, chatOverlayContext?.chatUuid, download, notify],
  );

  return {
    downloadCustomSkill,
    isDownloadingCustomSkill: useCallback(
      (skillUuid: string) => downloadingSkillUuid === skillUuid,
      [downloadingSkillUuid],
    ),
  };
}
